import { PlanType } from "../types/planType";

export const injectSortOrder = async (
  plans: PlanType[],
  stateCode: string | undefined,
  utilityId: string
) => {
  if (stateCode === "PA") {
    if (Object.prototype.hasOwnProperty.call(planOrder, utilityId)) {
      const orderedPlanNames = planOrder[utilityId as PlanOrderKey];
      const orderedPlanMap = new Map<string, number>();
      orderedPlanNames.forEach((planName, index) => {
        orderedPlanMap.set(planName, index);
      });

      // Step 1: Pre-process the plans to create a temporary, unambiguous sort name.

      // First, identify the cheaper and more expensive "FlexStart Intro" plans.
      // Using parseFloat is crucial for a correct numeric comparison.
      const flexStartPlans = plans
        .filter((p) => p.plan_name.trim() === "FlexStart Intro")
        .sort((a, b) => a.price2000 - b.price2000);

      const flexStartIntro1_planId = flexStartPlans[0]?.plan_id;
      const flexStartIntro3_planId = flexStartPlans[1]?.plan_id;

      // Create a new array with an attached 'sort_name' property.
      const plansWithSortName = plans.map((plan) => {
        let sort_name = plan.plan_name.trim(); // Default sort name is the plan name

        if (plan.plan_name.trim() === "FlexStart Intro") {
          if (plan.plan_id === flexStartIntro1_planId) {
            sort_name = "FlexStart Intro 1"; // Assign temporary name for sorting
          } else if (plan.plan_id === flexStartIntro3_planId) {
            sort_name = "FlexStart Intro 3"; // Assign temporary name for sorting
          }
        }
        return { ...plan, sort_name };
      });

      // Step 2: Sort using the simple 'sort_name' property.
      plansWithSortName.sort((a, b) => {
        const aIndex =
          orderedPlanMap.get(a.sort_name) ?? Number.MAX_SAFE_INTEGER;
        const bIndex =
          orderedPlanMap.get(b.sort_name) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });

      // Step 3: Return the original plan objects in the new, correct order.
      // This is done by mapping back and removing the temporary 'sort_name'.
      const sortedPlans = plansWithSortName.map(
        ({ sort_name, ...originalPlan }) => originalPlan
      );

      return sortedPlans;
    }
  }

  // If not PA or utilityId is not in the order, return plans as is.
  return plans;
};

const planOrder = {
  "39": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "40": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Eco Rewards 12",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "42": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "43": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "44": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "45": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "46": [
    "FlexStart Intro 1",
    "Embrace Green 12",
    "FlexStart Intro 3",
    "Embrace Green 24",
    "Embrace Green 6",
    "Affordable Wind 12",
    "Ultra Clean Solar 24",
  ],
  "64": [
    "Affordable Clean Gas 24",
    "Affordable Clean Gas 12",
    "Affordable Clean Gas 6",
    "Ultra Clean Gas 24",
    "Ultra Clean Gas 12",
  ],
  "66": [
    "Gas Giveback 12",
    "Affordable Clean Gas 24",
    "Affordable Gas 6",
    "Affordable Clean Gas 12",
    "Ultra Clean Gas 12",
    "Ultra Clean Gas 24",
  ],
  "93": [
    "Affordable Clean Gas 24",
    "Affordable Clean Gas 6",
    "Affordable Clean Gas 12",
    "Ultra Clean Gas 24",
    "Ultra Clean Gas 12",
  ],
  "95": [
    "FlexStart Intro 3",
    "Embrace Green 12",
    "Embrace Green 24",
    "Embrace Green 6",
    "Ultra Clean Solar 24",
    "Affordable Wind 12",
  ],
  "110": [
    "Affordable Clean Gas 12",
    "Affordable Clean Gas 6",
    "Affordable Clean Gas 24",
    "Ultra Clean Gas 24",
    "Ultra Clean Gas 12",
  ],
  "122": [
    "Affordable Clean Gas 6",
    "Affordable Clean Gas 12",
    "Affordable Clean Gas 24",
    "Ultra Clean Gas 24",
    "Ultra Clean Gas 12",
  ],
} as const;

type PlanOrderKey = keyof typeof planOrder;
